import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { auth } from '../firebase';
import { getMe } from '../api/dashboard';
import { errorCode, setUnauthenticatedHandler } from '../api/client';

export const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

// Why the nurse was sent back to the login page, shown there as a banner.
export const NOTICES = {
  not_a_dashboard_user: "Your account isn't set up for the Aren Health System Dashboard. Contact your Aren administrator.",
  no_unit: "You aren't assigned to a unit yet. Ask your nurse leader to add you to your unit.",
  idle: 'You were signed out after 15 minutes of inactivity.',
  expired: 'Your session expired. Please sign in again.',
  profile_error: "We couldn't load your account. Please try again.",
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // 'loading'   → waiting for Firebase to report the initial auth state
  // 'checking'  → signed in to Firebase, loading /api/me
  // 'signedIn'  → nurse profile loaded
  // 'signedOut'
  const [status, setStatus] = useState('loading');
  const [nurse, setNurse] = useState(null);
  const [notice, setNotice] = useState(null);
  const currentUid = useRef(null);
  // The last account signed in on this tab, kept after sign-out, so a page remembered
  // across a timeout is reopened only for that same account (see RequireAuth, Login).
  const lastUid = useRef(null);

  // The auth listener clears nurse and status together once Firebase confirms,
  // so signed-in screens never render without a nurse.
  const signOut = useCallback(async (reason = null) => {
    setNotice(reason);
    await firebaseSignOut(auth);
  }, []);

  useEffect(() => {
    setUnauthenticatedHandler(() => signOut('expired'));
    return () => setUnauthenticatedHandler(() => {});
  }, [signOut]);

  useEffect(() => {
    return onAuthStateChanged(auth, async (user) => {
      currentUid.current = user?.uid || null;
      if (user) lastUid.current = user.uid;
      if (!user) {
        setNurse(null);
        setStatus('signedOut');
        return;
      }
      setStatus('checking');
      try {
        const me = await getMe();
        if (currentUid.current !== user.uid) return;
        setNurse(me);
        setNotice(null);
        setStatus('signedIn');
      } catch (error) {
        if (currentUid.current !== user.uid) return;
        const code = errorCode(error);
        // A 401 here is already handled by the client's unauthenticated handler.
        if (code !== 'unauthenticated') {
          signOut(code === 'not_a_dashboard_user' || code === 'no_unit' ? code : 'profile_error');
        }
      }
    });
  }, [signOut]);

  // Idle auto sign-out while signed in.
  useEffect(() => {
    if (status !== 'signedIn') return undefined;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => signOut('idle'), IDLE_TIMEOUT_MS);
    };
    reset();
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, reset, { passive: true }));
    return () => {
      clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, reset));
    };
  }, [status, signOut]);

  const signIn = useCallback((email, password) => {
    setNotice(null);
    return signInWithEmailAndPassword(auth, email.trim(), password);
  }, []);

  const resetPassword = useCallback((email) => sendPasswordResetEmail(auth, email.trim()), []);

  // After the profile page saves, so the sidebar shows the new name.
  const updateNurse = useCallback((changes) => {
    setNurse((current) => (current ? { ...current, ...changes } : current));
  }, []);

  const value = useMemo(
    () => ({ status, nurse, notice, signIn, signOut, resetPassword, updateNurse, lastUid: () => lastUid.current }),
    [status, nurse, notice, signIn, signOut, resetPassword, updateNurse]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

// Admins see the whole organization; charge nurses see their own unit. This
// only shapes the UI: the backend enforces both on every route.
export function useIsAdmin() {
  return useContext(AuthContext)?.nurse?.role === 'admin';
}
